import React from 'react';
import { Form as AriaForm } from 'react-aria-components';

import { tv } from '../../tv';
import { FormLayoutContext, type LabelPosition } from '../field/field-shell';

const formStyles = tv({
  slots: {
    root: 'ck:font-sans',
    actions: 'ck:flex ck:flex-wrap ck:items-center ck:gap-2',
  },
  variants: {
    labelPosition: {
      top: { root: 'ck:flex ck:flex-col ck:gap-4' },
      /* Baseline alignment puts each label on its control's first line of
       * text, whatever the control's height. Every direct child that is not
       * a field (the actions, a form-level error) goes in the control
       * column; a field's root is `contents`, so its own cells are placed by
       * the field instead. */
      side: {
        root: [
          'ck:grid ck:grid-cols-label ck:items-baseline ck:gap-x-6 ck:gap-y-4',
          'ck:[&>*]:col-start-2',
        ],
      },
    },
  },
});

export interface FormProps {
  /** The fields, then FormActions. */
  children: React.ReactNode;
  /**
   * Where every field's label sits: `top` (stacked, the default) or `side`
   * (a label column beside the controls, for settings-style pages). The form
   * decides for all its fields, so one form never mixes the two.
   */
  labelPosition?: LabelPosition;
  /** Called on submit. Pass a form library's handler (`handleSubmit(save)`). */
  onSubmit?: (e: React.FormEvent<HTMLFormElement>) => void;
  /** Called on reset. */
  onReset?: (e: React.FormEvent<HTMLFormElement>) => void;
  /** The form's id, for a submit button outside it (a modal footer). */
  id?: string;
  /** Names the form for assistive technology when no heading does. */
  'aria-label'?: string;
  /** The id of the heading that names the form. */
  'aria-labelledby'?: string;
  /** The browser autofill default for every field. */
  autoComplete?: 'on' | 'off';
}

/**
 * A form: the fields in one layout, with validation owned by the caller.
 *
 * Validation is `aria` for every field inside: nothing native blocks the
 * submit, `aria-invalid` and `aria-required` carry the state, and the
 * caller's form library decides what is invalid and when (field.md).
 */
export const Form = React.forwardRef<HTMLFormElement, FormProps>(function Form(
  { children, labelPosition = 'top', ...props },
  ref,
) {
  const { root } = formStyles({ labelPosition });
  return (
    <FormLayoutContext.Provider value={labelPosition}>
      <AriaForm
        {...props}
        ref={ref}
        validationBehavior="aria"
        className={root()}
        data-component="form"
      >
        {children}
      </AriaForm>
    </FormLayoutContext.Provider>
  );
});

export interface FormActionsProps {
  /** The form's buttons, primary first. */
  children: React.ReactNode;
}

/**
 * The buttons of a form that sits in the page (a settings section). They
 * line up with the controls: under them when labels are on top, in the
 * control column when labels are at the side. A form in a modal puts its
 * buttons in the modal's footer instead, reaching the form by its `id`.
 */
export const FormActions = React.forwardRef<HTMLDivElement, FormActionsProps>(function FormActions(
  { children },
  ref,
) {
  const { actions } = formStyles();
  return (
    <div ref={ref} className={actions()} data-component="form-actions">
      {children}
    </div>
  );
});
